import { ReactNode } from 'react';
import { renderToString } from 'react-dom/server';

interface PrintProps {
  printWindow?: Window;
  title?: string;
  content?: ReactNode;
  printInSeparatePage?: boolean;
  pageSize?: string;
  pageMargin?: string;
  extraStyles?: string;
  fontScale?: number;
}

const print = ({
  printWindow: printWindowParam = undefined,
  content = null,
  title = undefined,
  printInSeparatePage = true,
  pageSize = undefined,
  pageMargin = '1.5cm',
  extraStyles = '',
  fontScale = 1,
}: PrintProps) => {
  const printWindow = printWindowParam || window.open('', '_blank');
  if (!printWindow) {
    throw new Error('Failed to open print window');
  }

  const contentString = renderToString(content);

  const html = `
    <html>
      <head>
        <meta charset="utf-8" />
        ${title ? `<title>${title}</title>` : ''}
        <style>
          * {
            box-sizing: border-box;
          }

          html {
            font-family: sans-serif;
            font-size: calc(16px * ${fontScale});
          }

          ul {
            display: flex;
            flex-direction: column;
            gap: 0.375rem;
          }

          h1 {
            font-size: 1.5rem;
            font-weight: 600;
            margin-bottom: 1rem;
          }

          li {
            font-weight: 500;
            font-size: 1.125rem;
          }

          table {
            width: 100%;
            border-collapse: collapse;
          }

          th {
            font-weight: 600;
          }

          td, th {
            font-size: 0.75rem;
            border: 1px solid #ccc;
            padding: 0.25rem 0.5rem;
          }

          tfoot {
            font-weight: 500;
          }

          tfoot td {
            font-weight: 600;
          }

          th, td, tr {
            vertical-align: middle;
            text-align: left;
          }

          .print-in-separate-page {
            page-break-after: ${printInSeparatePage ? 'always' : 'auto'};
          }

          .print-in-separate-page:last-of-type {
            page-break-after: auto;
          }

          .indent-cell {
            padding-left: 0.5rem; 
          }
          
          .bold-text-cell {
            font-weight: 600;
          }

          .flex-between {
            display: flex;
            justify-content: space-between;
            align-items: center;
          }

          .flex-row {
            display: flex;
          }

          .keep-together {
            page-break-inside: avoid;
          }

          @page {
            ${pageSize ? `size: ${pageSize};` : ''}
            margin: ${pageMargin};
          }

          ${extraStyles}
        </style>
      </head>
      <body>
        <main>
          ${contentString}
        </main>
      </body>
    </html>
  `;

  printWindow.document.write(html);
  printWindow.print();
  printWindow.close();
};

export default print;
